import { ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import {
  MAT_DIALOG_DATA, MatDialog,
  MatDialogConfig, MatDialogRef
} from '@angular/material/dialog';
import { ParseSocialService } from 'app/social/parse-social.service';
import { isNilOrEmpty, isValidURL } from 'helpers';

import type { ParseSocialResult } from 'app/social/ParseSocialResult';
import { finalize, map } from 'rxjs/operators';
import {
  AddSocialProfileGQL, AddSocialProfileInput, FieldStatusInput,
  SocialNetworkDetailsFragment,
  SocialProfileDetails,
  UpdateSocialProfileGQL, UpdateSocialProfileInput
} from 'generated/graphql';
import { SocialNetworksService } from 'app/social/get-social-networks.service';
import { ToastrService } from 'core/toastr.service';

/**
 * Format of data for the dialog
 */
export interface AddOrUpdateSocialDialogData {
  campaignId: string;
  entityId: string;
  isCreate: boolean;
  socialProfile?: any;
}

@Component({
  selector: 'app-add-or-update-social-dialog',
  templateUrl: './add-or-update-social.dialog.html',
})
export class AddOrUpdateSocialDialog implements OnInit {
  static open(
    dialog: MatDialog,
    config: MatDialogConfig<AddOrUpdateSocialDialogData>
  ) {
    return dialog.open(AddOrUpdateSocialDialog, config);
  }

  parsedResult: ParseSocialResult;

  isSaving = false;
  socialTypes = this.getSocialNetworks;
  verifiedFC = new FormControl(
    this.data.socialProfile && this.data?.socialProfile?.status === 'verified' ? true : false
  );
  selectedNetworkFC = new FormControl(
    this.data.socialProfile && this.data?.socialProfile?.socialNetwork?.id || null, {
    validators: [Validators.required]
  }
  );

  profileFC = new FormControl(
    this.data.socialProfile && this.data?.socialProfile?.profile || null, {
    validators: [Validators.required]
  }
  );

  labelFC = new FormControl(
    this.data.socialProfile && this.data?.socialProfile?.label || null, {
    validators: [Validators.required]
  }
  );

  formGroup = new FormGroup({
    network: this.selectedNetworkFC,
    profile: this.profileFC,
    verified: this.verifiedFC,
    label: this.labelFC,
  });

  get getSocialNetworks(): SocialNetworkDetailsFragment[] {
    return SocialNetworksService.socialNetworks;
  }

  get selectedNetwork(): string {
    return this.selectedNetworkFC?.value || null;
  }

  /** If no social data is provided, it is a create action */
  get isCreate() {
    return this.data.isCreate;
  }

  constructor(
    public dialogRef: MatDialogRef<AddOrUpdateSocialDialog>,
    private _parseSocialSvc: ParseSocialService,
    private _cd: ChangeDetectorRef,
    private _addSocialProfile: AddSocialProfileGQL,
    private _updateSocialProfile: UpdateSocialProfileGQL,
    private _toastrService: ToastrService,
    @Inject(MAT_DIALOG_DATA) public data: AddOrUpdateSocialDialogData
  ) {
  }

  ngOnInit(): void {
    this.setParsedResultOnInit();
    this.formGroup.valueChanges.subscribe(v => {
      if (v.network && v.profile) {
        const res = this.getParsedResult(v.network, v.profile);
        this.parsedResult = res;
        this._cd.markForCheck();
      } else {
        this.parsedResult = null;
      }
    });
  }

  setParsedResultOnInit() {
    const fv = this.formGroup.value;
    if(fv.network && fv.profile) {
      this.parsedResult = this.getParsedResult(fv.network, fv.profile);
    }
  }

  getParsedResult(network: string, profile: string) {
    return this._parseSocialSvc.parseSocial(network, profile);
  }

  getSocialIconClass(network: string) {
    return this.socialTypes.find(st => st.id === network)?.icon;
  }

  getSocialName(network: string) {
    return this.socialTypes.find(st => st.id === network)?.label;
  }

  onSaveClicked() {
    this._save();
  }

  isValid() {
    if(this.selectedNetwork === 'other') {
      return  this.parsedResult &&  this.profileFC.value
              && isValidURL(this.parsedResult.profileId) && this.labelFC.value;
    }
    if( this.selectedNetwork === 'website') {
      return this.parsedResult &&  this.profileFC.value  && isValidURL(this.parsedResult.profileId);
    }
    return this.parsedResult?.isValid;
  }


  close(data: any = null) {
    this.dialogRef.close(data);
  }

  private _save() {
    if (this.isSaving) {
      return;
    }
    this.isSaving = true;

    (this.isCreate ? this._addSocialNetwork() : this._updateSocialNetwork()).pipe(
      finalize(() => {
        this.isSaving = false;
        this.close();
      })
    ).subscribe(
      (result) => {
        this._toastrService.success('Changes saved');
        this.close(result);
      },
      (err) => {
        this._toastrService.error(err);
      }
    );
  }


  private _updateSocialNetwork() {
    const fv = this.formGroup.value;
    const input: UpdateSocialProfileInput = {
      campaignId: this.data.campaignId,
      entityId: this.data.entityId,
      socialProfileId: this.data?.socialProfile.id,
      profile: this.parsedResult.profileId,
      label: fv.label  && fv.network === 'other'  ? fv.label : this.getSocialName(fv.network),
      status: fv.verified ? FieldStatusInput.Verified : FieldStatusInput.UserAdded,
    };

    return this._updateSocialProfile.mutate({ input }).pipe(
      map(res => res.data?.updateSocialProfile?.entity)
    );
  }

  private _addSocialNetwork() {
    const fv = this.formGroup.value;
    const input: AddSocialProfileInput = {
      campaignId: this.data.campaignId,
      entityId: this.data.entityId,
      profile: this.parsedResult.profileId,
      networkType: this.parsedResult.profile,
      status: fv.verified ? FieldStatusInput.Verified : FieldStatusInput.UserAdded,
      label: fv.label && fv.network === 'other' ? fv.label : this.getSocialName(fv.network),
    };

    return this._addSocialProfile.mutate({ input }).pipe(
      map(res => res.data?.addSocialProfile?.entity)
    );
  }
}
